import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  CheckCircle as CheckCircleIcon,
  LinkOff as LinkOffIcon,
  SmartToy as SmartToyIcon,
  Visibility,
  VisibilityOff,
  Info as InfoIcon,
} from '@mui/icons-material';
import SettingsRowShell from './SettingsRowShell';
import {
  getGitHubConnectUrl,
  getGitHubStatus,
  disconnectGitHub,
  saveCopilotToken,
  deleteCopilotToken,
} from '../../services/githubService';

const GitHubRow = ({ onStatusChange }) => {
  const [ghStatus, setGhStatus] = useState(null);
  const [ghLoading, setGhLoading] = useState(true);
  const [ghError, setGhError] = useState('');

  const [copilotPat, setCopilotPat] = useState('');
  const [showPat, setShowPat] = useState(false);
  const [patSaving, setPatSaving] = useState(false);
  const [patError, setPatError] = useState('');
  const [patSuccess, setPatSuccess] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      setGhLoading(true);
      const status = await getGitHubStatus();
      setGhStatus(status);
      setGhError('');
    } catch (err) {
      setGhError('Failed to load GitHub status');
    } finally {
      setGhLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Listen for OAuth popup callback
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'GITHUB_OAUTH_CALLBACK') {
        if (event.data.success) {
          loadStatus();
          onStatusChange?.();
        } else {
          setGhError(event.data.error || 'Connection failed');
        }
        setGhLoading(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loadStatus, onStatusChange]);

  const handleConnect = async () => {
    setGhLoading(true);
    setGhError('');
    try {
      const { auth_url } = await getGitHubConnectUrl();
      const w = 600, h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(auth_url, 'github_oauth', `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`);
      if (!popup) {
        setGhError('Popup blocked. Please allow popups for this site.');
        setGhLoading(false);
      }
      const interval = setInterval(() => {
        if (popup?.closed) { clearInterval(interval); setGhLoading(false); }
      }, 500);
    } catch (err) {
      setGhError(err.response?.data?.detail || 'Failed to start GitHub OAuth');
      setGhLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setGhLoading(true);
    try {
      await disconnectGitHub();
      setGhStatus((prev) => ({ ...prev, connected: false, username: null, avatar_url: null }));
      onStatusChange?.();
    } catch (err) {
      setGhError('Failed to disconnect');
    }
    setGhLoading(false);
  };

  const handleSavePat = async () => {
    if (!copilotPat.trim()) return;
    setPatSaving(true);
    setPatError('');
    setPatSuccess('');
    try {
      await saveCopilotToken(copilotPat.trim());
      setPatSuccess('Copilot PAT saved and encrypted successfully');
      setCopilotPat('');
      setGhStatus((prev) => ({ ...prev, has_copilot_token: true }));
      onStatusChange?.();
    } catch (err) {
      setPatError(err.response?.data?.detail || 'Failed to save PAT');
    }
    setPatSaving(false);
  };

  const handleRemovePat = async () => {
    setPatSaving(true);
    setPatError('');
    try {
      await deleteCopilotToken();
      setGhStatus((prev) => ({ ...prev, has_copilot_token: false }));
      setPatSuccess('');
      onStatusChange?.();
    } catch (err) {
      setPatError('Failed to remove PAT');
    }
    setPatSaving(false);
  };

  const isConnected = ghStatus?.connected;
  const hasCopilotToken = ghStatus?.has_copilot_token;

  const statusChip = (() => {
    if (ghLoading && !ghStatus) return { label: 'Loading…', color: 'default' };
    if (isConnected && hasCopilotToken) return { label: `${ghStatus.username} + PAT`, color: 'success' };
    if (isConnected) return { label: ghStatus.username, color: 'success' };
    return { label: 'Not Connected', color: 'warning' };
  })();

  return (
    <SettingsRowShell
      icon={<GitHubIcon />}
      title="GitHub & Copilot"
      description="Repository access and AI token for QAssure AI"
      status={statusChip}
      loading={ghLoading && !ghStatus}
    >
      {/* ---- Section 1: GitHub OAuth ---- */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          <GitHubIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
          Repository Access
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Connect your GitHub account to browse repos, read code, and commit changes.
        </Typography>

        {ghError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setGhError('')}>
            {ghError}
          </Alert>
        )}

        {isConnected ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.light' }}>
            <Avatar src={ghStatus.avatar_url} sx={{ width: 36, height: 36 }}>
              <GitHubIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>{ghStatus.username}</Typography>
              <Chip icon={<CheckCircleIcon />} label="Connected" color="success" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            </Box>
            <Button variant="outlined" color="error" size="small" startIcon={<LinkOffIcon />} onClick={handleDisconnect} disabled={ghLoading}>
              Disconnect
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            size="small"
            startIcon={ghLoading ? <CircularProgress size={16} color="inherit" /> : <GitHubIcon />}
            onClick={handleConnect}
            disabled={ghLoading}
            sx={{ bgcolor: '#24292e', '&:hover': { bgcolor: '#1b1f23' }, textTransform: 'none' }}
          >
            {ghLoading ? 'Connecting…' : 'Connect GitHub Account'}
          </Button>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ---- Section 2: Copilot PAT ---- */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          <SmartToyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
          Copilot AI Token <Chip label="Optional" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', ml: 0.5, verticalAlign: 'middle' }} />
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Add a fine-grained GitHub PAT with <strong>"Copilot Requests"</strong> permission to use your own AI quota.
        </Typography>

        {patError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setPatError('')}>{patError}</Alert>}
        {patSuccess && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setPatSuccess('')}>{patSuccess}</Alert>}

        {hasCopilotToken ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <CheckCircleIcon color="success" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>Copilot PAT configured</Typography>
              <Typography variant="caption" color="text.secondary">Using your own AI quota</Typography>
            </Box>
            <Button variant="outlined" color="error" size="small" onClick={handleRemovePat} disabled={patSaving}>
              Remove
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Fine-grained GitHub PAT"
              placeholder="github_pat_..."
              type={showPat ? 'text' : 'password'}
              value={copilotPat}
              onChange={(e) => setCopilotPat(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPat(!showPat)}>
                      {showPat ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSavePat}
              disabled={!copilotPat.trim() || patSaving}
              startIcon={patSaving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.25 }}
            >
              {patSaving ? 'Saving…' : 'Save PAT'}
            </Button>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ---- Info ---- */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ fontSize: '0.8rem' }}>
        <Typography variant="caption" component="div">
          <strong>GitHub OAuth</strong> — Browse and commit to repos. Doesn't expire.
          <br />
          <strong>Copilot PAT</strong> — Fine-grained PAT with "Copilot Requests" permission. Uses <em>your</em> quota instead of shared org token.
          <br />
          <Link href="https://github.com/settings/tokens?type=beta" target="_blank" rel="noopener" sx={{ fontSize: '0.75rem' }}>
            Create a fine-grained PAT →
          </Link>
        </Typography>
      </Alert>
    </SettingsRowShell>
  );
};

export default GitHubRow;
